import { assign, isNumber } from 'radash';
import {
  findAndReplaceElementTrackItemInPlayerConfig,
  updateElementTransition
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transition.utils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { mergeDeletes } from '@goldcast/player-config/src/utils/assetMetadataUtils';
import {
  getClipFPS,
  getClipReadOnlyPlayerConfig,
  getClipVersion,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { updateClip } from '@goldcast/player-config/src/retrievers/updateClip';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { ClipDeletes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { CaptionStyle, CaptionLineConfig } from '@goldcast/player-config/src/types/asset-types';
import { Scene } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { getCompositeSegment } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { DEFAULT_FONT_SIZE } from '@goldcast/player-config/src/constants/player';
import { getCaptionPreset } from '@goldcast/player-config/src/caption-presets/utils';
import {
  getPlayerConfigElementById,
  getPlayerConfigMetadata
} from '@goldcast/player-config/src/utils/playerConfigUtils';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { ImageElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { approxEquals } from '@goldcast/player-config/src/utils/compareUtils';
import {
  getNewTrimFromEndDeletes,
  getNewTrimFromStartDeletes
} from '../../ClipSpeakersTimeline/ClipSpeakersTimelineTrim/utlis';
import { AddSegmentTrackAction, UpdateClipType } from './types';
import { CLIP_UPDATE_TYPE } from './constants';
import { syncDeletesAcrossLanguages } from './syncDeletesAcrossLanguages';
import { addCompositeSegmentDelete, getClipCompositeSegment } from './compositeUtils';
import {
  addNewSceneAtFrame,
  addNewSceneAtRange,
  createNewScene,
  createNewSceneAtRange,
  deleteScene,
  getDefaultSceneData,
  toggleVideoVisibilityFromScene,
  trimScenes
} from './sceneUtils';

import { updateCaptionPositions, updateCaptions, getDefaultCaptionStyles } from './captionUtils';
import { updateCurrentClipUpdatedAt } from './updatedAtUtils';
import { shouldAddToHistory } from './historyUtils';
import { applyWordEdit, applyWordHide, applyWordShow, applyCorrectAll } from './transcriptUtils';
import { updateClipDataWithSplits } from '../clipUpdateDataUtilsWithSplits/updateWithSplits';
import { UpdateClipType as SplitsUpdateClipType } from '../clipUpdateDataUtilsWithSplits/types';
import {
  upsertSegmentTransition,
  removeSegmentTransition,
  updateTransitionsWhenSegmentAdded,
  updateTransitionsWhenSegmentRemoved
} from '../clipUpdateDataUtilsWithSplits/segmentTransitionUtils';
import appLogger from '@/services/logger';
import { markCaptionsObsolete } from '@/stores/captions';
import { allClips, currentClip, templateLoader, trackClipUpdate, updateClipCaptionsLoadingState } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { debounce } from '@/libs/utils';
import { restoreDelete } from '@/components/molecules/Transcript/EditTranscriptMenu/utils';
import { updateHistory } from '@/stores/clipHistory';
import { getLayoutAvailabilityStatus } from '@/context/ClipsContext/ClipsContextUtils';
import { addPendingUpdate } from '@/stores/pendingEditsStore';
import { getPlayerConfigHelper } from '@/App/remotion/MainPlayer/utils/playerConfig/config';

const debouncedUpdateClipApi = debounce(
  ({ clip, onSuccess, version }: { clip: Clip; onSuccess: (updatedClip: Clip) => void; version: number }) => {
    addPendingUpdate({ clipId: clip.id, updatePromise: updateClip(clip).then(onSuccess), version });
  }
);

/**
 * Updates the clip data based on the provided update action.
 * Handles various update types such as adding/removing segment tracks,
 * updating composite segment deletes, and updating scene crop positions.
 * Also manages history, triggers thumbnail generation, and updates captions as needed.
 *
 * @param {UpdateClipType} updateClipPayload - The payload describing the update action and clip id.
 * @param {boolean} [shouldSkipSave=false] - If true, skips saving the updated clip to the backend or other stores. Used when are using some drag interactions like deleting words, deleting composite segment deletes, etc.
 * @returns {Clip} The updated clip object.
 */
/**
 * @example
 * updateClipData({
 *   clipId: 'clip-123',
 *   actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SIZE,
 *   size: 'PORTRAIT'
 * });
 *
 * @throws {Error} When action type is not implemented
 * @returns {Clip} The updated clip object
 */

export const updateClipData = (
  updateClipPayload: UpdateClipType,
  options: { skipStoreAndBackendUpdate?: boolean; skipBackendUpdate?: boolean; currentClipData?: Clip | null } = {
    skipStoreAndBackendUpdate: false,
    skipBackendUpdate: false,
    currentClipData: null
  }
) => {
  const { skipStoreAndBackendUpdate = false, skipBackendUpdate = false } = options;
  const { clipId, actionType } = updateClipPayload;
  const currentClipsData = currentClip.getSnapshot();
  const oldClip = options.currentClipData || currentClipsData[clipId];
  const newClip = structuredClone(oldClip);
  const eventsToDispatch: Record<string, boolean | Record<string, any>> = {};
  const addToHistory = shouldAddToHistory();
  const isSegmentTransitionsEnabled = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Segment_Transitions];
  let shouldSyncDeletes = false;

  const isSplitsEnabledBeforeOperation = isSplitsInTimelineEnabledPure({
    clip: oldClip,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  const afterOperationSplitsWillBeEnabled =
    actionType === CLIP_UPDATE_TYPE.SPLIT_SEGMENT ||
    (actionType === CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK && (updateClipPayload as AddSegmentTrackAction).id === null);

  if (isSplitsEnabledBeforeOperation || afterOperationSplitsWillBeEnabled) {
    // Ensure payload has type property for segment track actions
    let splitsPayload: SplitsUpdateClipType = updateClipPayload as SplitsUpdateClipType;
    if (actionType === CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK || actionType === CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK) {
      splitsPayload = { ...updateClipPayload, type: SegmentType.VIDEO } as SplitsUpdateClipType;
    }
    return updateClipDataWithSplits(splitsPayload, options);
  }

  newClip.asset_metadata.is_edited = true;
  newClip.asset_metadata.isLayoutProcessing =
    getLayoutAvailabilityStatus(newClip.asset_metadata.layout, newClip.layout_status, newClip, true) === 'PROCESSING';

  switch (actionType) {
    case CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK: {
      const { id: segmentId, url, duration } = updateClipPayload;
      if (segmentId) {
        let normalizedDuration = isNumber(duration) ? Math.floor(duration) : 0;
        const fps = getClipFPS(newClip);
        normalizedDuration = Math.floor(normalizedDuration * fps) / fps;
        newClip.asset_metadata[segmentId] = url;
        newClip.asset_metadata[segmentId + 'Duration'] = normalizedDuration;
        if (oldClip.asset_metadata[segmentId] !== url) {
          eventsToDispatch['UPDATE_INTRO_OUTRO'] = true;
        }
        if (!isSegmentTransitionsEnabled) {
          break;
        }
        if (segmentId === 'intro') {
          updateTransitionsWhenSegmentAdded(newClip, 'fromSegmentId', 'intro');
        } else if (segmentId === 'outro') {
          updateTransitionsWhenSegmentAdded(newClip, 'toSegmentId', 'outro');
        }
      }

      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK: {
      const { id: segmentId } = updateClipPayload;
      if (segmentId === 'intro' || segmentId === 'outro') {
        newClip.asset_metadata[segmentId] = undefined;
        newClip.asset_metadata[segmentId + 'Duration'] = 0;
        if (!isSegmentTransitionsEnabled) {
          break;
        }
        if (segmentId === 'intro') {
          updateTransitionsWhenSegmentRemoved(newClip, 'fromSegmentId', 'intro');
        } else if (segmentId === 'outro') {
          updateTransitionsWhenSegmentRemoved(newClip, 'toSegmentId', 'outro');
        }
      }
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK: {
      const { startTime, endTime, type, duration } = updateClipPayload;

      if (type !== SegmentType.COMPOSITE) {
        throw new Error('Not implemented');
      }
      if (startTime !== undefined) {
        const boundedStartTime = Math.max(0, startTime);
        newClip.asset_metadata.start = boundedStartTime;
        const newDeletes = getNewTrimFromStartDeletes(boundedStartTime, newClip);
        if (newDeletes) {
          newClip.asset_metadata.deletes = newDeletes;
        }
      }
      const maxVideoDuration = duration || newClip.asset_metadata.maxVideoDuration;
      if (endTime !== undefined && maxVideoDuration) {
        const boundedEndTime = Math.min(endTime, maxVideoDuration);
        newClip.asset_metadata.end = boundedEndTime;
        const newDeletes = getNewTrimFromEndDeletes(boundedEndTime, newClip, maxVideoDuration);
        if (newDeletes) {
          newClip.asset_metadata.deletes = newDeletes;
        }
      }
      if (approxEquals(newClip.asset_metadata.start, newClip.asset_metadata.end)) {
        throw new Error('Cannot reduce the duration to 0');
      }
      eventsToDispatch['FULL_RECORDING_TRIM'] = true;
      shouldSyncDeletes = true;

      // !! @Hitesh dispatch DURATION_CHANGED event
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE: {
      addCompositeSegmentDelete(newClip, updateClipPayload);

      const oldStart = roundToNDecimalPlaces(oldClip.asset_metadata.start, 3);
      const oldEnd = roundToNDecimalPlaces(oldClip.asset_metadata.end, 3);
      const newStart = roundToNDecimalPlaces(newClip.asset_metadata.start, 3);
      const newEnd = roundToNDecimalPlaces(newClip.asset_metadata.end, 3);

      if (newStart !== oldStart || newEnd !== oldEnd) {
        eventsToDispatch['DURATION_CHANGED'] = true;
      }
      eventsToDispatch['FULL_RECORDING_TRANSCRIPT_DELETE'] = true;
      shouldSyncDeletes = true;

      break;
    }
    case CLIP_UPDATE_TYPE.BULK_ADD_COMPOSITE_SEGMENT_DELETES: {
      const { deletes } = updateClipPayload;
      newClip.asset_metadata.deletes = mergeDeletes({ ...newClip.asset_metadata.deletes, ...deletes });
      eventsToDispatch['FULL_RECORDING_TRANSCRIPT_DELETE'] = true;
      shouldSyncDeletes = true;

      break;
    }
    case CLIP_UPDATE_TYPE.BULK_PUT_COMPOSITE_SEGMENT_DELETE: {
      const { deletes } = updateClipPayload;
      newClip.asset_metadata.deletes = mergeDeletes(deletes);
      eventsToDispatch['FULL_RECORDING_TRANSCRIPT_DELETE'] = true;
      shouldSyncDeletes = true;

      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SEGMENT_DELETE: {
      const { deleteId, endTime, startTime } = updateClipPayload;

      const newDeletes: ClipDeletes = structuredClone(oldClip.asset_metadata.deletes || {});
      delete newDeletes[deleteId];

      newDeletes[startTime] = {
        bounds: [startTime, endTime],
        prev_end_time: startTime,
        next_start_time: endTime
      };

      const mergeDeletesData = mergeDeletes(newDeletes);

      if (startTime <= oldClip.asset_metadata.start) {
        newClip.asset_metadata.start = endTime;
      }
      if (endTime >= oldClip.asset_metadata.end) {
        newClip.asset_metadata.end = startTime;
      }

      newClip.asset_metadata.deletes = mergeDeletesData;
      shouldSyncDeletes = true;

      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_COMPOSITE_SEGMENT_DELETE: {
      const { deleteId } = updateClipPayload;

      const deletes = structuredClone(oldClip.asset_metadata.deletes || {});
      delete deletes[deleteId];

      newClip.asset_metadata.deletes = deletes;
      shouldSyncDeletes = true;

      break;
    }
    case CLIP_UPDATE_TYPE.RESTORE_COMPOSITE_SEGMENT_DELETE: {
      const { startTime, endTime } = updateClipPayload;
      const absoluteStartTime = roundToNDecimalPlaces(Math.min(startTime, endTime), 3);
      const absoluteEndTime = roundToNDecimalPlaces(Math.max(startTime, endTime), 3);

      const newDeletes = restoreDelete(oldClip.asset_metadata.deletes || {}, {
        bounds: [absoluteStartTime, absoluteEndTime],
        next_start_time: absoluteEndTime,
        prev_end_time: absoluteStartTime
      });
      if (startTime <= oldClip.asset_metadata.start) {
        newClip.asset_metadata.start = startTime;
      }
      if (endTime >= oldClip.asset_metadata.end) {
        newClip.asset_metadata.end = endTime;
      }
      newClip.asset_metadata.deletes = newDeletes;
      shouldSyncDeletes = true;

      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE_WORDS: {
      addCompositeSegmentDelete(newClip, updateClipPayload);

      const oldStart = roundToNDecimalPlaces(oldClip.asset_metadata.start, 3);
      const oldEnd = roundToNDecimalPlaces(oldClip.asset_metadata.end, 3);
      const newStart = roundToNDecimalPlaces(newClip.asset_metadata.start, 3);
      const newEnd = roundToNDecimalPlaces(newClip.asset_metadata.end, 3);

      if (newStart !== oldStart || newEnd !== oldEnd) {
        eventsToDispatch['DURATION_CHANGED'] = true;
      }

      eventsToDispatch['DELETE_WORDS'] = true;
      shouldSyncDeletes = true;

      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_SCENE_CROP_POSITION: {
      const { sceneId, videoId, cropPosition } = updateClipPayload;
      if (sceneId && videoId) {
        const selectedScene = newClip.asset_metadata.scenes?.find(scene => scene.id === sceneId);
        if (selectedScene) {
          selectedScene.details.video_crop_position_map = {
            ...(selectedScene.details.video_crop_position_map || {}),
            [videoId]: cropPosition
          };
        }
      }
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE: {
      const { currentFrame } = updateClipPayload;
      const compositeSegment = getClipCompositeSegment(newClip);
      const scenes = compositeSegment.scenes || [];

      let updatedScenes: Scene[] = [];

      if (scenes.length === 0) {
        updatedScenes = createNewScene({ newClip, currentFrame });
      } else {
        updatedScenes = addNewSceneAtFrame({ newClip, currentFrame });
      }

      newClip.asset_metadata.scenes = updatedScenes;
      eventsToDispatch['ADD_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE_RANGE: {
      const { startFrame, endFrame } = updateClipPayload;
      const compositeSegment =
        newClip.asset_metadata.player_config && getCompositeSegment(newClip.asset_metadata.player_config);

      if (!compositeSegment) {
        throw new Error('No composite segment found');
      }

      let updatedScenes: Scene[] = [];
      const scenes = newClip.asset_metadata.scenes || [];

      if (scenes.length === 0) {
        updatedScenes = createNewSceneAtRange({ newClip, startTimeFrame: startFrame, endTimeFrame: endFrame });
      } else {
        updatedScenes = addNewSceneAtRange({ newClip, startTimeFrame: startFrame, endTimeFrame: endFrame });
      }
      newClip.asset_metadata.scenes = updatedScenes;
      eventsToDispatch['ADD_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_COMPOSITE_SCENE: {
      const { sceneId } = updateClipPayload;
      const updatedScenes = deleteScene({ newClip, sceneId });

      newClip.asset_metadata.scenes = updatedScenes;
      eventsToDispatch['DELETE_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SCENE: {
      const { sceneId, fromFrame, toFrame, layout, fitMode } = updateClipPayload;
      const scenes = newClip.asset_metadata.scenes || [];
      const sceneIndex = scenes.findIndex(scene => scene.id === sceneId);

      if (sceneIndex === -1) {
        throw new Error('Scene not found');
      }

      const updatedScenes = [...scenes];
      const scene = updatedScenes[sceneIndex];

      if (fromFrame !== undefined) {
        scene.fromFrame = fromFrame;
        const previousScene = updatedScenes[sceneIndex - 1];
        if (previousScene) {
          previousScene.toFrame = fromFrame;
        }
      }
      if (toFrame !== undefined) {
        scene.toFrame = toFrame;
        const nextScene = updatedScenes[sceneIndex + 1];
        if (nextScene) {
          nextScene.fromFrame = toFrame;
        }
      }
      if (layout) {
        scene.details.layout = layout;
      }
      if (fitMode) {
        scene.details.fitMode = fitMode;
      }

      newClip.asset_metadata.scenes = updatedScenes;
      if (updatedScenes.length === 1) {
        newClip.asset_metadata.layout = scene.details.layout;
        newClip.asset_metadata.fit = scene.details.fitMode;
      }
      eventsToDispatch['UPDATE_SCENE'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.TOGGLE_VIDEO_VISIBILITY_FROM_SCENE: {
      const { sceneId, trackName, videoId } = updateClipPayload;
      const updatedScenes = toggleVideoVisibilityFromScene({ newClip, sceneId, trackName, videoId });
      newClip.asset_metadata.scenes = updatedScenes;
      eventsToDispatch['UPDATE_SCENE'] = true;
      break;
    }

    case CLIP_UPDATE_TYPE.UPDATE_CLIP_FIT_MODE: {
      const { fitMode } = updateClipPayload;

      if (fitMode === oldClip.asset_metadata.fit) {
        break;
      }

      newClip.asset_metadata.fit = fitMode;

      const scenes = newClip.asset_metadata.scenes || [];
      if (scenes.length === 0) {
        const updatedScenes = getDefaultSceneData({ clip: newClip });
        newClip.asset_metadata.scenes = [updatedScenes];
      } else {
        scenes.forEach(scene => {
          scene.details.fitMode = fitMode;
        });
        newClip.asset_metadata.scenes = scenes;
      }
      eventsToDispatch['UPDATE_SCENE'] = true;

      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_BORDERS_VISIBILITY: {
      const { hideBorders } = updateClipPayload;
      newClip.asset_metadata.hide_borders = hideBorders;
      eventsToDispatch[hideBorders ? 'HIDE_BORDERS' : 'SHOW_BORDERS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_SIZE: {
      const { size } = updateClipPayload;
      newClip.asset_metadata.size = size;
      eventsToDispatch['CHANGE_SIZE'] = true;
      break;
    }
    // !!Deprecate after susnset of older players
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT: {
      const { layout } = updateClipPayload;

      const layoutStatus = getLayoutAvailabilityStatus(layout, oldClip.layout_status, oldClip, true);

      let updatedMetadata: Partial<Clip['asset_metadata']> = {
        layout: layout,
        size: oldClip.asset_metadata.size,
        isLayoutProcessing: layoutStatus === 'PROCESSING'
      };

      const isScaleDefined = isNumber(oldClip.asset_metadata.caption_styles?.scale);

      const defaultLineConfig = CaptionLineConfig.OneLine;

      // ! TODO @AshwinBhatkal isScaleDefined condition to be moved to updateClipResponse in clip.ts
      if (!isScaleDefined) {
        updatedMetadata = {
          ...updatedMetadata,
          caption_styles: {
            ...oldClip.asset_metadata.caption_styles,
            font_size: DEFAULT_FONT_SIZE,
            caps: oldClip.asset_metadata.caption_styles?.caps || false,
            animation: oldClip.asset_metadata.caption_styles?.animation || CaptionStyle.Outline,
            scale: 1,
            line_config: oldClip.asset_metadata.caption_styles?.line_config || defaultLineConfig
          }
        };
      }

      newClip.asset_metadata = {
        ...newClip.asset_metadata,
        ...updatedMetadata
      };
      eventsToDispatch['SELECT_MAGIC_LAYOUT'] = true;

      const scenes = newClip.asset_metadata.scenes || [];
      if ((layout === 'AUDIOGRAM' && scenes.length > 1) || scenes.length === 0) {
        const updatedScenes = getDefaultSceneData({ clip: newClip });
        newClip.asset_metadata.scenes = [updatedScenes];
      } else {
        scenes.forEach(scene => {
          scene.details.layout = layout;
        });
        newClip.asset_metadata.scenes = scenes;
      }
      newClip.asset_metadata.layout = layout;
      eventsToDispatch['UPDATE_SCENE'] = true;

      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABELS: {
      const { showSpeakerLabels } = updateClipPayload;

      if (newClip.asset_metadata.magicLayout) {
        newClip.asset_metadata.magicLayout = {
          ...(newClip.asset_metadata.magicLayout || {}),
          showSpeakerLabels
        };
        eventsToDispatch['TOGGLE_SPEAKER_LABELS'] = true;
        eventsToDispatch['UPDATE_MAGIC_LAYOUT'] = true;
      }
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABEL_STYLES: {
      const { speakerLabelsStyles } = updateClipPayload;
      const currentStyles = oldClip.asset_metadata.speaker_label_styles || {};
      const finalSpeakerLabelsStyles = {
        ...currentStyles,
        ...speakerLabelsStyles,
        containerStyle: {
          ...(currentStyles.containerStyle || {}),
          ...(speakerLabelsStyles.containerStyle || {})
        },
        nameStyle: {
          ...(currentStyles.nameStyle || {}),
          ...(speakerLabelsStyles.nameStyle || {})
        },
        titleStyle: {
          ...(currentStyles.titleStyle || {}),
          ...(speakerLabelsStyles.titleStyle || {})
        },
        transitions: currentStyles.transitions
          ? {
              ...currentStyles.transitions,
              ...(speakerLabelsStyles.transitions || {})
            }
          : speakerLabelsStyles.transitions
      };

      newClip.asset_metadata.speaker_label_styles = finalSpeakerLabelsStyles;
      eventsToDispatch['UPDATE_SPEAKER_LABEL_STYLES'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT: {
      const { updatePayload } = updateClipPayload;
      const clipMetadata = oldClip.asset_metadata;

      const hasHighlightType = Object.keys(updateClipPayload).includes('highlightType');
      const hasHighlightColor = Object.keys(updateClipPayload).includes('highlightColor');
      const hasTextHighlightColor = Object.keys(updateClipPayload).includes('textHighlightColor');

      const { highlightType, highlightColor, textHighlightColor } = updatePayload;
      if (hasHighlightType) {
        newClip.asset_metadata.highlight_type = highlightType;
        newClip.asset_metadata.word_highlight_color =
          highlightType === 'box' ? clipMetadata.word_highlight_color || '#000000' : clipMetadata.word_highlight_color;
        newClip.asset_metadata.text_highlight_color =
          highlightType !== 'none'
            ? clipMetadata.text_highlight_color || clipMetadata.magicLayout?.textColor || '#ffffff'
            : clipMetadata.text_highlight_color;
        eventsToDispatch['UPDATE_HIGHLIGHT_TYPE'] = true;
      }
      if (hasHighlightColor) {
        newClip.asset_metadata.word_highlight_color = highlightColor;
        eventsToDispatch['UPDATE_HIGHLIGHT_COLOR'] = true;
      }
      if (hasTextHighlightColor) {
        newClip.asset_metadata.text_highlight_color = textHighlightColor;
        eventsToDispatch['UPDATE_TEXT_HIGHLIGHT_COLOR'] = true;
      }
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CAPTIONS_POSITION: {
      const { captionPositions } = updateClipPayload;
      newClip.asset_metadata.caption_positions = captionPositions;
      eventsToDispatch['CHANGE_CAPTIONS_POSITION'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CAPTION_STYLES: {
      const { captionStyles } = updateClipPayload;
      if (newClip.asset_metadata.caption_styles) {
        newClip.asset_metadata.caption_styles = {
          ...(newClip.asset_metadata.caption_styles || {}),
          ...captionStyles
        };
      } else {
        newClip.asset_metadata.caption_styles = {
          ...getDefaultCaptionStyles(newClip),
          // !! Hitesh need to confirm with kashis the reason for this value.
          animation: CaptionStyle.Basic,
          ...captionStyles
        };
      }
      eventsToDispatch['CAPTION_STYLES_V2'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.TOGGLE_CAPTIONS: {
      const { enabled } = updateClipPayload;

      newClip.asset_metadata.subtitle = enabled;
      newClip.asset_metadata.caption_styles = getDefaultCaptionStyles(newClip);
      eventsToDispatch[enabled ? 'CAPTIONS_ON' : 'CAPTIONS_OFF'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CAPTION_PRESET: {
      const { presetName } = updateClipPayload;
      newClip.asset_metadata.caption_styles = {
        ...getDefaultCaptionStyles(newClip),
        ...(newClip.asset_metadata.caption_styles || {}),
        ...getCaptionPreset(presetName)
      };
      newClip.asset_metadata.subtitle = true;
      eventsToDispatch['UPDATE_CAPTION_PRESET'] = { presetName };
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_CLIP_FONT_LOCATION: {
      const { path } = updateClipPayload;
      newClip.asset_metadata.font_location = path;
      newClip.asset_metadata.caption_styles = {
        ...getDefaultCaptionStyles(newClip),
        ...(newClip.asset_metadata.caption_styles || {}),
        font_location: path
      };
      eventsToDispatch['UPDATE_FONT_LOCATION'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_MAGIC_LAYOUT: {
      const { updatePayload } = updateClipPayload;
      if (newClip.asset_metadata.magicLayout) {
        newClip.asset_metadata.magicLayout = {
          ...newClip.asset_metadata.magicLayout,
          ...updatePayload
        };
      }
      eventsToDispatch['UPDATE_MAGIC_LAYOUT'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_VISIBLE_FACE_IDS: {
      const { visibleFaceIds } = updateClipPayload;
      newClip.asset_metadata.visible_face_ids = visibleFaceIds;
      eventsToDispatch['UPDATE_VISIBLE_FACE_IDS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_VISIBLE_SPEAKERS: {
      const { visibleSpeakers } = updateClipPayload;
      newClip.asset_metadata.visible_speakers = visibleSpeakers;
      eventsToDispatch['UPDATE_VISIBLE_SPEAKERS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_VISIBLE_VIDEO_IDS: {
      const { visibleVideoIds } = updateClipPayload;
      newClip.asset_metadata.visible_video_ids = visibleVideoIds;
      eventsToDispatch['UPDATE_VISIBLE_VIDEO_IDS'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_EFFECT: {
      const { effect } = updateClipPayload;

      const playerConfig = newClip.asset_metadata.player_config;
      if (playerConfig) {
        const compositeSegment = getCompositeSegment(playerConfig);
        if (compositeSegment) {
          compositeSegment.effects = [...(compositeSegment.effects || []), effect];
        }
      }
      eventsToDispatch['ADD_COMPOSITE_SEGMENT_EFFECT'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.EDIT_WORD: {
      const { words, newValue } = updateClipPayload;
      const updatedClip = applyWordEdit(newClip, words, newValue);
      Object.assign(newClip, updatedClip);
      markCaptionsObsolete({ clipId });
      updateClipCaptionsLoadingState({ clipId, loading: true });
      eventsToDispatch['EDIT_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.HIDE_WORD: {
      const { words } = updateClipPayload;
      const updatedClip = applyWordHide(newClip, words);
      Object.assign(newClip, updatedClip);
      markCaptionsObsolete({ clipId });
      updateClipCaptionsLoadingState({ clipId, loading: true });
      eventsToDispatch['HIDE_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.SHOW_WORD: {
      const { words } = updateClipPayload;
      const updatedClip = applyWordShow(newClip, words);
      Object.assign(newClip, updatedClip);
      markCaptionsObsolete({ clipId });
      updateClipCaptionsLoadingState({ clipId, loading: true });
      eventsToDispatch['SHOW_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.CORRECT_ALL: {
      const { paragraphs, editingContent, newValue, selectedWords } = updateClipPayload;
      const updatedClip = applyCorrectAll(newClip, paragraphs, editingContent, newValue, selectedWords);
      Object.assign(newClip, updatedClip);
      markCaptionsObsolete({ clipId });
      updateClipCaptionsLoadingState({ clipId, loading: true });
      eventsToDispatch['EDIT_WORD'] = true;
      break;
    }
    case CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION: {
      const { elementId, transitionLifeCycle, updateLifeCycleTransition } = updateClipPayload;
      const playerConfig = getClipReadOnlyPlayerConfig(newClip);
      if (!playerConfig) {
        throw new Error('Player config not found');
      }
      let element = getPlayerConfigElementById(playerConfig, elementId) as ImageElement | VideoElement;
      if (!element) {
        throw new Error('Element not found');
      }
      element = updateElementTransition({
        element,
        lifeCycleType: transitionLifeCycle,
        lifeCycleTransition: updateLifeCycleTransition,
        fps: getClipFPS(newClip)
      });
      const updatedPlayerConfig = findAndReplaceElementTrackItemInPlayerConfig(playerConfig, elementId, element);
      newClip.asset_metadata.player_config = updatedPlayerConfig;
      break;
    }
    case CLIP_UPDATE_TYPE.UPSERT_SEGMENT_TRANSITION_OUT: {
      const { transition } = updateClipPayload;
      upsertSegmentTransition(newClip, transition);
      break;
    }
    case CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRANSITION_OUT: {
      const { fromSegmentId, toSegmentId } = updateClipPayload;
      removeSegmentTransition(newClip, fromSegmentId, toSegmentId);
      break;
    }
    case CLIP_UPDATE_TYPE.UPDATE_PLAYER_CONFIG_METADATA: {
      const { metadata: newMetadata } = updateClipPayload;
      const currentMetadata = getPlayerConfigMetadata(getClipReadOnlyPlayerConfig(newClip));

      // Recursive merge
      const updatedMetadata = assign(currentMetadata ?? {}, newMetadata);

      const playerConfig = getClipReadOnlyPlayerConfig(newClip);
      if (!playerConfig) {
        throw new Error('Player config not found');
      }

      playerConfig.playerConfig.metadata = updatedMetadata;
      break;
    }
    default: {
      throw new Error('Not implemented');
    }
  }

  updateCaptions(newClip);
  trimScenes(newClip);
  updateCaptionPositions(newClip);

  const newPlayerConfig = getPlayerConfigHelper({ clip: newClip, shouldUpdatePlayerConfig: true });

  newClip.asset_metadata.player_config = newPlayerConfig;
  newClip.asset_metadata.remotionConfig = null;

  const version = getClipVersion();
  newClip.version = version;

  if (skipStoreAndBackendUpdate) {
    return newClip;
  }

  // dispatch events
  Object.keys(eventsToDispatch).forEach(event => {
    const eventPayload: any[] = [clipId, event];
    if (typeof eventsToDispatch[event] === 'object') {
      eventPayload.push(eventsToDispatch[event]);
    }
    trackClipUpdate(...(eventPayload as [string, string, Record<string, any> | undefined]));
  });

  currentClip.update(data => {
    data[clipId] = newClip;
    return data;
  });

  allClips.update(allClipsData => allClipsData.map(clip => (clip.id === newClip.id ? newClip : clip)));

  // When are are not updating the backend, we can don't want to generate thumbnail or add to history.
  if (skipBackendUpdate) {
    return newClip;
  }

  if (addToHistory) {
    updateHistory(clipId, newClip.asset_metadata, undefined, actionType);
  }

  // after save call change updated_at flag.

  const onClipUpdateSuccess = (updatedClip: Clip) => {
    templateLoader.set(() => false);
    updateCurrentClipUpdatedAt(updatedClip);
  };

  if (!skipBackendUpdate) {
    debouncedUpdateClipApi({
      clip: currentClip.getSnapshot()[clipId],
      onSuccess: onClipUpdateSuccess,
      version
    });
  }

  // Sync deletes across languages if needed
  const isSplitTranslationsEnabled = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations];
  if (
    isSplitTranslationsEnabled &&
    shouldSyncDeletes &&
    !skipBackendUpdate &&
    !skipStoreAndBackendUpdate &&
    newClip.asset_metadata.deletes
  ) {
    syncDeletesAcrossLanguages(newClip).catch(error => {
      appLogger.error('Error syncing deletes across languages:', error);
    });
  }

  return newClip;

  //  on failure errors
};
